import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment/moment';

const apiDate = (val) => moment(val).format('YYYY-MM-DD HH:mm:ss');

const normalizeMulti = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .flatMap((v) => {
      if (v == null) return [];
      const s = String(v).trim();
      if (!s) return [];
      // split in case it turns out "a,b"
      return s.includes(',') ? s.split(',').map((x) => x.trim()) : [s];
    })
    .filter((v) => v.length > 0);

// Universal parsers/serializers
const fromUrlByType = (params, def) => {
  if (def.type === 'range') {
    const [fk, tk] = def.keys;
    const fromDate = params.get(fk);
    const toDate = params.get(tk);
    return fromDate && toDate ? { fromDate, toDate } : {};
  }
  if (def.type === 'multi') {
    return normalizeMulti(params.getAll(def.key));
  }
  if (def.type === 'single') {
    const v = params.get(def.key);
    return v ? [v] : [];
  }
  return undefined;
};

const toUrlByType = (value, prev, def) => {
  if (def.type === 'range') {
    const [fk, tk] = def.keys;
    if (value?.fromDate && value?.toDate) {
      prev.set(fk, value.fromDate);
      prev.set(tk, value.toDate);
    }
  } else if (def.type === 'multi') {
    prev.delete(def.key);
    const list = normalizeMulti(value);
    list.forEach((v) => prev.append(def.key, v));
  } else if (def.type === 'single') {
    prev.delete(def.key);
    if (value?.length) {
      prev.set(def.key, String(value[0]));
    }
  }
};
const toApiByType = (value, acc, key, def) => {
  if (def.type === 'range') {
    if (value?.fromDate && value?.toDate) {
      acc.start_dt = apiDate(value.fromDate);
      acc.end_dt = apiDate(value.toDate);
    }
  } else if (def.type === 'multi') {
    if (Array.isArray(value) && value.length) {
      acc[key] = value.slice();
    }
  } else if (def.type === 'single' && value?.length) {
    const raw = value[0];
    acc[key] = def.mapApi ? def.mapApi(raw) : raw;
  }
};

// Delete all filter parameters
const deleteFilterParamsFromUrl = (prev, schema) => {
  Object.values(schema).forEach((def) => {
    if (def.type === 'range') {
      def.keys.forEach((k) => prev.delete(k));
    } else {
      prev.delete(def.key);
    }
  });
};

// Build API params from URL
const getFilterParams = (schema, initSettings) => {
  const url = new URLSearchParams(window.location.search);
  const filters = { ...initSettings };
  Object.entries(schema).forEach(([k, def]) => {
    filters[k] = fromUrlByType(url, def);
  });

  const api = {};
  Object.entries(schema).forEach(([k, def]) =>
    toApiByType(filters[k], api, k, def)
  );
  return api;
};

/**
 * React hook that synchronizes filter state with the URL query string and provides
 * helpers to serialize for API requests.
 *
 * It maps URL <-> state using a schema that defines filter types:
 * - "range": date range stored under two URL keys. State shape: { fromDate?: string, toDate?: string } or {}.
 * - "multi": multiple values. State shape: string[].
 * - "single": single value. State shape: [string] or [].
 *
 * Schema entry fields:
 * - type: "range" | "multi" | "single"
 * - key?: string                // URL key for "single" / "multi"
 * - keys?: [string, string]     // URL keys for "range" as [fromKey, toKey]
 * - mapApi?: (value: string) => any // optional transformer before sending to API
 *
 * @template {Record<string, {
 *   type: 'range' | 'multi' | 'single',
 *   key?: string,
 *   keys?: [string, string],
 *   mapApi?: (value: string) => any
 * }>} TSchema
 *
 * @param {TSchema} schema
 *   Declarative mapping of filter fields to URL representation.
 *
 * @param {Partial<Record<keyof TSchema, any>>} initSettings
 *   Initial in-memory defaults for filters (used when URL lacks values).
 *
 * @returns {{
 *   filters: Record<keyof TSchema, any>,
 *   setFilters: (next: Partial<Record<keyof TSchema, any>>) => void,
 *   params: URLSearchParams,
 *   setParams: import('react-router-dom').SetURLSearchParams
 * }}
 *   - filters: current filters derived from URL per schema.
 *   - setFilters: updates URL params based on provided filter values (overwrites relevant keys).
 *   - params: raw URLSearchParams from react-router.
 *   - setParams: low-level setter for URLSearchParams.
 */

const useFiltersUrlState = (schema, initSettings) => {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => {
    const f = { ...initSettings };
    Object.entries(schema).forEach(([k, def]) => {
      f[k] = fromUrlByType(params, def);
    });
    return f;
  }, [params]);

  const setFilters = useCallback(
    (newFilters) => {
      setParams((prev) => {
        deleteFilterParamsFromUrl(prev, schema);
        Object.entries(schema).forEach(([k, def]) => {
          toUrlByType(newFilters[k], prev, def);
        });
        return prev;
      });
    },
    [setParams]
  );

  return { filters, setFilters, params, setParams };
};

// see example in src/components/Incidents/FilterIncidents/urlConfig.js

export { useFiltersUrlState, getFilterParams, deleteFilterParamsFromUrl };
